import { Loader, Libraries } from "@googlemaps/js-api-loader";
import { useEffect, useState } from "react";

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? "";

/**
 * Libraries and language for the Maps JS API.
 *
 * These MUST stay identical to the `bootstrapURLKeys` passed to GoogleMapReact
 * in components/Map/Map.tsx. google-map-react builds its own Loader out of
 * those keys, and @googlemaps/js-api-loader is a singleton that throws if it is
 * ever constructed a second time with different options. Keeping both call
 * sites in agreement is what lets the map and the places autocomplete share a
 * single script injection.
 */
export const GOOGLE_MAPS_LIBRARIES: Libraries = ["places"];
export const GOOGLE_MAPS_LANGUAGE = "en";

let loadPromise: Promise<void> | null = null;

export function isGoogleMapsLoaded(): boolean {
  if (typeof window === "undefined") return false;
  return !!(window as any).google?.maps?.places;
}

/**
 * Loads the Maps JS API on demand, once per session.
 *
 * This used to be a render-blocking <script> in index.html, so every visitor
 * fetched the Maps SDK — and counted as a billable Maps load — even on pages
 * with no map on them at all, the landing page included.
 */
export function loadGoogleMaps(): Promise<void> {
  if (isGoogleMapsLoaded()) return Promise.resolve();

  if (!loadPromise) {
    loadPromise = new Loader({
      apiKey: API_KEY,
      libraries: GOOGLE_MAPS_LIBRARIES,
      language: GOOGLE_MAPS_LANGUAGE,
    })
      .load()
      .then(() => undefined)
      .catch((error) => {
        // Clear the cache so a later mount can retry rather than being stuck
        // with a permanently rejected promise.
        loadPromise = null;
        throw error;
      });
  }

  return loadPromise;
}

/**
 * Loads the Maps JS API and re-renders when it is usable. Components that touch
 * `google.maps` at runtime should hold off until this returns true.
 */
export function useGoogleMaps(): boolean {
  const [loaded, setLoaded] = useState(isGoogleMapsLoaded);

  useEffect(() => {
    if (loaded) return;

    let active = true;
    loadGoogleMaps()
      .then(() => {
        if (active) setLoaded(true);
      })
      .catch((error) => {
        console.error("Google Maps failed to load", error);
      });

    return () => {
      active = false;
    };
  }, [loaded]);

  return loaded;
}
